import { motion } from "framer-motion";
import { gsap } from "gsap";
import { useEffect, useRef } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { TiFlash, TiGlobe, TiLightbulb } from "react-icons/ti";
import GlassButton from "./ui/GlassButton";

const CorePrincipleModal = ({ isOpen, onClose, principle }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        modalRef.current,
        { scale: 0.8, opacity: 0, y: 20 },
        { 
          scale: 1, 
          opacity: 1, 
          y: 0, 
          duration: 0.5, 
          ease: "power3.out" 
        }
      );
    }
  }, [isOpen]);

  if (!isOpen || !principle) return null;

  const getIcon = (type) => {
    switch (type) {
      case "Integrity First": return <FaShieldAlt className="text-cyan-400" />;
      case "Radical Innovation": return <TiLightbulb className="text-purple-400" />;
      case "Global Inclusivity": return <TiGlobe className="text-emerald-400" />;
      default: return <TiFlash className="text-cyan-400" />;
    }
  };

  const getColorClass = (type) => {
    switch (type) {
      case "Integrity First": return "cyan";
      case "Radical Innovation": return "purple";
      case "Global Inclusivity": return "emerald";
      default: return "cyan";
    }
  };

  const color = getColorClass(principle.title);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
      />
      
      <motion.div
        ref={modalRef}
        className="relative w-full max-w-2xl bg-gray-900/90 border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl"
      >
        {/* Decorative background glow */}
        <div className={`absolute -top-24 -right-24 w-64 h-64 bg-${color}-500/10 rounded-full blur-3xl`} />
        
        {/* Header */}
        <div className="relative p-8 md:p-12">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all cursor-pointer"
          >
            <IoClose size={24} />
          </button>
          
          <div className={`w-16 h-16 rounded-2xl bg-${color}-500/10 flex items-center justify-center text-4xl mb-6 border border-white/5`}>
            {getIcon(principle.title)}
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {principle.title}
          </h2>
          
          <p className="text-blue-100/80 text-lg leading-relaxed mb-8">
            {principle.desc}
          </p>
          
          <div className="space-y-6">
            <div>
              <h4 className={`text-sm font-bold uppercase tracking-widest text-${color}-400 mb-3`}>Key Objectives</h4>
              <ul className="grid md:grid-cols-2 gap-3">
                {["Systemic Trust", "Open Source Heritage", "Verifiable Decentralisation", "Immutable Records"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-blue-50">
                    <div className={`w-1.5 h-1.5 rounded-full bg-${color}-400`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
              <h4 className="text-white font-bold mb-2">Strategic Impact</h4>
              <p className="text-sm text-blue-100/70 leading-relaxed">
                By adhering to this core principle, we ensure that every product within the Zero Ecosystem 
                contributes to a more equitable and efficient global financial layer. Our engineering 
                standards are built around these pillars to guarantee long-term stability and growth.
              </p>
            </div>
          </div>
          
          <div className="mt-10 flex justify-end">
            <GlassButton
              onClick={onClose}
              variant="gray"
              className="px-8 py-3"
            >
              Close Details
            </GlassButton>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CorePrincipleModal;
